/**
 * persistence/vcs/MergeManager.js
 *
 * The "merge / resolve" half of the git-style layer.  Two operations, one
 * review-and-approve UX:
 *
 *   mode "merge"   — combine another analyst's branch into the current branch.
 *                    This is a true CRDT merge: clone the target HEAD, apply the
 *                    source's Yjs update, and Yjs converges automatically.  The
 *                    only thing a human needs to resolve is *semantic*
 *                    duplicates — the same logical event appended on both sides
 *                    survives as two array entries; we surface those for pruning.
 *
 *   mode "restore" — bring a past commit/version back into the current branch.
 *                    A CRDT merge can't do this (an older state is a causal
 *                    ancestor, so applying it is a no-op), so restore is a
 *                    forward edit: set the working tokens to the chosen
 *                    snapshot's tokens, recorded as a normal commit.
 *
 * Both modes:
 *   previewMerge() → a result doc + a human token-diff (added/removed/dupes)
 *                    for the dialog, without mutating anything.
 *   applyMerge()   → re-materialises the previewed result (optionally pruning
 *                    duplicates the user chose), commits it, repoints the live
 *                    doc, and returns the merged string for Redux.
 */

import {
  loadDoc,
  encodeDoc,
  getEventsArray,
  setDocTokens,
  docToString,
  docToTokens,
  stringToTokens,
} from "./yjsDoc";
import * as Y from "yjs";
import { computeDiff } from "../managers/DiffManager";
import {
  getBranch,
  getCommit,
  getOrCreateDefaultBranch,
  commit as commitBranch,
} from "./BranchManager";
import { setLiveDoc } from "./StringBridge";

/**
 * @typedef {Object} MergeSource
 * @property {"branch"|"commit"} type
 * @property {string=} branchName   when type === "branch"
 * @property {string=} commitId     when type === "commit"
 * @property {string=} label        human label for the dialog
 */

// ─── Preview ────────────────────────────────────────────────────────────────

/**
 * Computes a non-destructive preview of merging `source` into the target branch.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.targetBranchName
 * @param {MergeSource} params.source
 * @param {"merge"|"restore"} params.mode
 * @returns {Promise<object>} preview descriptor (see file header)
 */
export async function previewMerge({
  matchId,
  analystId,
  targetBranchName,
  source,
  mode,
}) {
  const targetBranch =
    (await getBranch(matchId, analystId, targetBranchName)) ??
    (await getOrCreateDefaultBranch(matchId, analystId));

  const targetDoc = loadDoc(targetBranch.stateUpdate);
  const targetString = docToString(targetDoc);

  const { stateUpdate: sourceUpdate, tokens: sourceTokens, ref: sourceRef } =
    await _resolveSource(matchId, analystId, source);

  // Work on a clone so the preview never touches the real branch HEAD.
  const resultDoc = loadDoc(targetBranch.stateUpdate);

  if (mode === "restore") {
    setDocTokens(resultDoc, sourceTokens);
  } else {
    if (sourceUpdate) Y.applyUpdate(resultDoc, sourceUpdate);
    else setDocTokens(resultDoc, sourceTokens); // fallback if no binary state
  }

  const mergedString = docToString(resultDoc);
  const diff = computeDiff(targetString, mergedString);

  return {
    mode,
    matchId,
    analystId,
    targetBranchName: targetBranch.branchName,
    targetHeadCommitId: targetBranch.headCommitId ?? null,
    sourceRef,
    sourceLabel: source.label ?? sourceRef ?? "source",
    resultStateUpdate: encodeDoc(resultDoc),
    targetString,
    mergedString,
    addedTokens: diff.addedEvents,
    removedTokens: diff.removedEvents,
    possibleDuplicates:
      mode === "merge"
        ? _findDuplicates(docToTokens(targetDoc), sourceTokens, docToTokens(resultDoc))
        : [],
  };
}

// ─── Apply ──────────────────────────────────────────────────────────────────

/**
 * Materialises a previewed merge: optionally prunes user-selected duplicate
 * events, commits the result on the target branch, repoints the live doc, and
 * returns the merged event string (to be dispatched into Redux so the editor
 * continues from the merged state).
 *
 * @param {object} params
 * @param {object} params.preview        the object returned by previewMerge
 * @param {string[]=} params.dedupeTokens tokens the user chose to de-duplicate
 * @param {string=} params.message        commit message override
 * @returns {Promise<{string: string, commitId: string|null}>}
 */
export async function applyMerge({ preview, dedupeTokens = [], message }) {
  const { matchId, analystId, targetBranchName, mode } = preview;

  const doc = loadDoc(preview.resultStateUpdate);

  // Prune the extra copies the user flagged (merge mode only).
  if (mode === "merge" && dedupeTokens.length > 0) {
    const dupMap = new Map(preview.possibleDuplicates.map((d) => [d.token, d.extra]));
    doc.transact(() => {
      for (const token of dedupeTokens) {
        _removeTrailingOccurrences(doc, token, dupMap.get(token) ?? 0);
      }
    });
  }

  // Parent linkage: merge commits record both parents (targetHead + source).
  const targetHead = preview.targetHeadCommitId;
  let parentCommitId = targetHead ?? undefined;
  if (mode === "merge" && targetHead && preview.sourceRef) {
    parentCommitId = `${targetHead}+${preview.sourceRef}`;
  }

  const defaultMsg =
    mode === "restore"
      ? `Restore ${preview.sourceLabel}`
      : `Merge ${preview.sourceLabel}`;

  const committed = await commitBranch({
    matchId,
    analystId,
    branchName: targetBranchName,
    message: message ?? defaultMsg,
    doc,
    parentCommitId,
  });

  // Repoint the in-memory live doc so subsequent edits build on the merge.
  setLiveDoc(matchId, analystId, doc, targetBranchName);

  return { string: docToString(doc), commitId: committed?.commitId ?? null };
}

// ─── Internal ─────────────────────────────────────────────────────────────────

/**
 * Resolves a merge source descriptor into { stateUpdate, tokens, ref }.
 *   - commit:  load the commit snapshot
 *   - branch:  load the branch HEAD state
 */
async function _resolveSource(matchId, analystId, source) {
  if (source.type === "commit") {
    const c = await getCommit(source.commitId);
    if (!c) return { stateUpdate: null, tokens: [], ref: source.commitId };
    return {
      stateUpdate: c.stateUpdate,
      tokens: stringToTokens(docToString(loadDoc(c.stateUpdate))),
      ref: c.commitId,
    };
  }
  // branch
  const b = await getBranch(matchId, analystId, source.branchName);
  if (!b) return { stateUpdate: null, tokens: [], ref: source.branchName };
  return {
    stateUpdate: b.stateUpdate,
    tokens: docToTokens(loadDoc(b.stateUpdate)),
    ref: b.headCommitId ?? source.branchName,
  };
}

/**
 * Identifies events that the merge duplicated: tokens whose count in the merged
 * result exceeds the most either side held on its own.  Those extras are almost
 * always "we both tagged the same moment" and are the only thing a human needs
 * to resolve after a CRDT merge.
 *
 * @returns {Array<{token: string, extra: number}>}
 */
function _findDuplicates(targetTokens, sourceTokens, mergedTokens) {
  const count = (arr) => {
    const m = new Map();
    for (const t of arr) m.set(t, (m.get(t) ?? 0) + 1);
    return m;
  };
  const tc = count(targetTokens);
  const sc = count(sourceTokens);
  const mc = count(mergedTokens);

  const dupes = [];
  for (const [token, merged] of mc) {
    const expected = Math.max(tc.get(token) ?? 0, sc.get(token) ?? 0);
    if (merged > expected) dupes.push({ token, extra: merged - expected });
  }
  return dupes;
}

/**
 * Removes the last `n` occurrences of `token` from the doc's events array.
 * Removing trailing copies keeps the earliest (original) occurrence stable.
 * Must be called inside a doc.transact().
 */
function _removeTrailingOccurrences(doc, token, n) {
  if (n <= 0) return;
  const yarr = getEventsArray(doc);
  let removed = 0;
  for (let i = yarr.length - 1; i >= 0 && removed < n; i--) {
    if (yarr.get(i) === token) {
      yarr.delete(i, 1);
      removed++;
    }
  }
}
